<template>
  <form @submit.prevent="handleSubmit" class="side-wrapper side-wrapper-flex">
    <!-- Header -->
    <div class="side-header">
      <div class="side-wrapper-subtitle">{{ generalStore.viewHeader }}</div>
      <h1 v-if="form.id">{{ $gettext("Edit user") }} '{{ form.first_name }}'</h1>
      <h1 v-else>{{ $gettext("Create user") }}</h1>
    </div>

    <!-- Body -->
    <div class="side-body">
      <!-- First name -->
      <NiceInput
        :title="$gettext('First name')"
        v-model="form.first_name"
        type="text"
        :required="true"
        :placeholder="$gettext('John')"
        :disabled="form.id"
      />

      <!-- Last name -->
      <NiceInput
        :title="$gettext('Last time')"
        v-model="form.last_name"
        type="text"
        :required="true"
        :placeholder="$gettext('Doe')"
        :disabled="form.id"
      />

      <!-- Email -->
      <NiceInput
        :title="$gettext('Email')"
        v-model="form.email"
        type="email"
        :required="true"
        :placeholder="$gettext('john@doe.com')"
        :disabled="form.id"
      />

      <!-- Language -->
      <NiceDropdown
        :title="$gettext('Language')"
        v-model="form.language"
        :values="CommonService.languages"
        :key-only="true"
        :required="true"
        v-if="!form.id"
      />

      <!-- Limited to specific events -->
      <NiceSwitch :title="$gettext('Limited to specific events')" v-model="form.is_limited_to_specific_events" />

      <!-- Events -->
      <NiceDropdown
        v-if="form.is_limited_to_specific_events"
        :title="$gettext('Limited to events')"
        v-model="form.events"
        :searchFunction="searchEvents"
        multiple
        required
      >
        <template #option="{ item }">
          <div>
            <div>{{ item.name }}</div>
            <small>{{ formatDateWithTime(item.start_time, false, true) }}</small>
          </div>
        </template>
        <template #selected-option="{ item }">
          <div>
            <div>{{ item.name }}</div>
            <small>{{ formatDateWithTime(item.start_time, false, true) }}</small>
          </div>
        </template>
      </NiceDropdown>

      <!-- Permissions -->
      <UserPermissions v-model="form.permissions" />
      <!-- <pre>{{ form }}</pre> -->
    </div>

    <!-- Footer -->
    <div class="side-footer">
      <NiceActions @cancel="cancel" :loading="loading" :showDelete="!!form.id && form.can_delete" @delete="handleDelete" />
    </div>
  </form>
</template>

<script setup>
import { defineProps, onMounted, defineEmits, ref } from "vue";
const clone = require("clone");
import { formatDateWithTime } from "@/plugins/date";

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useRouter } from "vue-router";
const router = useRouter();

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();

import ApiService from "@/services/api/api.service";
import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import UserPermissions from "@/components/UserPermissions.vue";

const emit = defineEmits(["cancel", "success"]);
const props = defineProps({
  data: {
    type: Object,
    required: false,
  },
});
const form = ref({
  first_name: null,
  last_name: null,
  email: null,
  language: null,
  permissions: {
    can_view_partners: true,
    can_view_users: true,
    can_manage_users: true,
    can_create_events: true,
    can_create_guests: true,
    can_create_guest_groups: true,
    can_create_partners: true,
    can_edit_events: true,
    can_edit_guests: true,
    can_edit_guest_groups: true,
    can_edit_partners: true,
    can_deactivate_guests: true,
    can_reactivate_guests: true,
    can_checkin_guests: true,
    can_confirm_guests: true,
  },
  is_limited_to_specific_events: false,
  events: [],
});
const loading = ref(false);
const organizationId = router.currentRoute.value.params.organizationId;

async function searchEvents(keyword) {
  return ApiService.events.getAll(organizationId, { search: keyword });
}

async function handleSubmit() {
  loading.value = true;
  try {
    if (form.value.id) {
      form.value = await ApiService.users.update(organizationId, form.value);
      CommonService.success($gettext("User was updated"));
    } else {
      form.value = await ApiService.users.create(organizationId, form.value);
      CommonService.success($gettext("User was created"));
    }
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

async function handleDelete() {
  loading.value = true;
  try {
    form.value = await ApiService.users.delete(organizationId, form.value.id);
    CommonService.success($gettext("User was deleted"));
    emit("success");
  } catch (error) {
    ErrorService.showError(error);
  } finally {
    loading.value = false;
  }
}

function cancel() {
  emit("cancel");
}

onMounted(async () => {
  const userId = router.currentRoute.value.query.id;
  if (userId == "new") return;
  if (props.data) return (form.value = clone(props.data));

  try {
    form.value = await ApiService.users.get(organizationId, userId);
  } catch (e) {
    emit("cancel");
  }
});
</script>
